import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

export type DeleteReasonOption = { label: string; value: string };

export interface DeleteConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  confirmLabel?: string;
  loading?: boolean;
  /** When true, shows a required delete-reason field; confirm stays disabled until filled. */
  requireDeleteReason?: boolean;
  deleteReason?: string;
  onDeleteReasonChange?: (value: string) => void;
  deleteReasonLabel?: string;
  /** Dropdown mode: options from Code Common; `selectedDeleteReasonCode` is `codevalue`. */
  deleteReasonOptions?: DeleteReasonOption[];
  selectedDeleteReasonCode?: string;
  onDeleteReasonCodeChange?: (value: string) => void;
  deleteRemark?: string;
  onDeleteRemarkChange?: (value: string) => void;
  /** `codevalue` for the "Other" option; remark is required when this is selected. */
  deleteReasonOtherCodeValue?: string;
  deleteRemarkLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Shared confirm-delete dialog (matches Staff / Hub delete UX). */
const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  title = "Confirm Delete",
  message,
  confirmLabel = "Yes, Delete",
  loading = false,
  requireDeleteReason = false,
  deleteReason = "",
  onDeleteReasonChange,
  deleteReasonLabel = "Delete reason",
  deleteReasonOptions,
  selectedDeleteReasonCode = "",
  onDeleteReasonCodeChange,
  deleteRemark = "",
  onDeleteRemarkChange,
  deleteReasonOtherCodeValue = "",
  deleteRemarkLabel = "Remark",
  onCancel,
  onConfirm,
}) => {
  const useDropdown =
    Array.isArray(deleteReasonOptions) && deleteReasonOptions.length > 0;

  const otherCode = String(deleteReasonOtherCodeValue ?? "").trim();
  const isOtherSelected =
    useDropdown &&
    otherCode !== "" &&
    String(selectedDeleteReasonCode ?? "").trim() === otherCode;

  const reasonTrimmed = deleteReason.trim();
  const remarkTrimmed = deleteRemark.trim();
  const codeTrimmed = String(selectedDeleteReasonCode ?? "").trim();

  const reasonValid = useDropdown
    ? codeTrimmed !== "" && (!isOtherSelected || remarkTrimmed !== "")
    : !requireDeleteReason || reasonTrimmed !== "";

  const confirmDisabled = loading || !reasonValid;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
    >
      <DialogTitle id="delete-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText
          id="delete-dialog-description"
          sx={{ mb: requireDeleteReason || useDropdown ? 2 : 0 }}
        >
          {message}
        </DialogContentText>
        {useDropdown ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth size="small" disabled={loading} required>
              <InputLabel id="delete-reason-select-label">
                {deleteReasonLabel}
              </InputLabel>
              <Select
                labelId="delete-reason-select-label"
                label={deleteReasonLabel}
                value={selectedDeleteReasonCode}
                onChange={(e) =>
                  onDeleteReasonCodeChange?.(String(e.target.value))
                }
              >
                {deleteReasonOptions!.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={
                isOtherSelected
                  ? `${deleteRemarkLabel} (required)`
                  : `${deleteRemarkLabel} (optional)`
              }
              value={deleteRemark}
              onChange={(e) => onDeleteRemarkChange?.(e.target.value)}
              required={isOtherSelected}
              fullWidth
              multiline
              minRows={2}
              size="small"
              disabled={loading}
              placeholder={
                isOtherSelected
                  ? "Enter remark for deleting this refund"
                  : "Optional remark"
              }
              inputProps={{ "aria-label": deleteRemarkLabel }}
            />
          </Box>
        ) : requireDeleteReason ? (
          <TextField
            label={deleteReasonLabel}
            value={deleteReason}
            onChange={(e) => onDeleteReasonChange?.(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            placeholder="Enter reason for deleting this refund"
            inputProps={{ "aria-label": deleteReasonLabel }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{ color: "rgba(13, 44, 90, 1)" }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirmDisabled}
          variant="contained"
          sx={{
            backgroundColor: "rgba(197, 0, 0, 1)",
            "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            confirmLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
